import * as React from "react"
import { Link } from "react-router-dom"
import { Box } from "@mui/material"
import { CircularPercentageSeeker, IconSeeker, TooltipSeeker } from "@ui-seeker/core"
import { formatNumber } from "@ui-seeker/utils"

import { NotAllowedBlock } from "@components"


export default function DigitalShelfTableCustomHeadLabel({ label, value, prev, diff, showDiff, help }) {
  
	const circularProps = {
		pattern: [
			{ value: 89, color: "success.light" },
			{ value: 79, color: "success.lighter" },
			{ value: 69, color: "warning.lighter" },
			{ value: 49, color: "warning.light" },
			{ value: 29, color: "error.lighter" },
			{ value: "else", color: "error.light" }
		]
	}

	const color = diff === 0 
		? "disabled.dark"
		: diff > 0 
			? "success.main" 
			: "error.main"

	const score = showDiff ? diff : value

	const formatter = showDiff ? (val) => formatNumber(val, { decimals: 1 }) : null

	return <Box>
		{value !== null
			? <CircularPercentageSeeker
				value={value}
				valueShow={showDiff ? diff : value}
				previous={prev}
				format={formatter}
				sx={{
					...showDiff && {
						".circle .insideCircle": {
							color: color
						}
					}
				}}
				{...circularProps}
			/>
			: <NotAllowedBlock />
		}
		<DigitalShelfTableHeadLabelWithHelp label={label} help={help} />
	</Box>
}


/**
 * The column name plus its `?`. Split out so the N/A headers can reuse it.
 *
 * The `?` both explains and navigates: hovering shows the summary, clicking opens the Info
 * Center glossary at this KPI's card. `help` comes from `DIGITAL_SHELF_KPI_HELP`.
 *
 * It is a real link and not a button, so the user decides where it opens: a plain click
 * navigates in place, middle click or Ctrl/Cmd + click opens a tab, and the context menu works.
 * Forcing `target="_blank"` would take that choice away.
 */
export function DigitalShelfTableHeadLabelWithHelp({ label, help }) {
	if (!help) return label

	const tooltip = <>
		<Box component="span" sx={{ whiteSpace: "pre-line" }}>{help.text}</Box>
		<Box component="span" sx={{ display: "block", mt: 0.5, fontStyle: "italic", opacity: 0.8 }}>
			Click to read the full definition
		</Box>
	</>

	return <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
		{label}
		<TooltipSeeker title={tooltip} placement="top">
			<Box
				component={Link}
				to={help.path}
				aria-label={`${label} — open the glossary`}
				onClick={event => event.stopPropagation()}
				sx={{
					display: "inline-flex",
					// Without this the anchor's default underline runs under the glyph and the
					// circle reads as if it had a tail.
					textDecoration: "none",
					color: "text.secondary",
					"&:hover": { color: "primary.main" }
				}}
			>
				<IconSeeker
					icon="circle-question"
					iconType="fontAwesome"
					iconWeight="regular"
					sx={{ fontSize: "0.9rem" }}
				/>
			</Box>
		</TooltipSeeker>
	</Box>
}